import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Search, Eye, Users, GraduationCap, CheckCircle2, AlertTriangle, Link as LinkIcon, ClipboardList, Settings, Sparkles, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatReceivedAt } from "../../lib/dateFormat";
import AssignmentDetailModal from "./AssignmentDetailModal";
import CreateAssignmentDialog from "./CreateAssignmentDialog";
import CreateDemoClassDialog from "./CreateDemoClassDialog";
import ClassroomHub from "./ClassroomHub";

/**
 * V156 — Classroom & Assignments (post-tutor-acceptance workflow).
 *
 * Reads from GET /api/admin/assignments. Filter tabs match the 7 buckets
 * defined server-side in assignments.FILTER_BUCKETS.
 */

const BUCKETS = [
    { id: "newly_accepted", label: "Newly Accepted", icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { id: "awaiting_setup", label: "Awaiting Setup", icon: ClipboardList, tone: "text-amber-700 bg-amber-50 border-amber-200" },
    { id: "parent_link_pending", label: "Parent Link Pending", icon: LinkIcon, tone: "text-blue-700 bg-blue-50 border-blue-200" },
    { id: "tutor_link_pending", label: "Tutor Link Pending", icon: LinkIcon, tone: "text-indigo-700 bg-indigo-50 border-indigo-200" },
    { id: "active", label: "Active", icon: GraduationCap, tone: "text-violet-700 bg-violet-50 border-violet-200" },
    { id: "completed", label: "Completed", icon: CheckCircle2, tone: "text-slate-700 bg-slate-100 border-slate-200" },
    { id: "sync_errors", label: "Sync Errors", icon: AlertTriangle, tone: "text-rose-700 bg-rose-50 border-rose-200" },
];

const STATUS_LABELS = {
    tutor_accepted: "Tutor Accepted — Awaiting Admin Setup",
    awaiting_admin_setup: "Awaiting Admin Setup",
    awaiting_parent_link: "Awaiting Parent Link",
    awaiting_tutor_link: "Awaiting Tutor Link",
    awaiting_schedule_confirm: "Awaiting Schedule Confirmation",
    demo_scheduled: "Demo Scheduled",
    demo_completed: "Demo Completed",
    awaiting_parent_approval: "Awaiting Parent Approval",
    active: "Active",
    paused: "Paused",
    completed: "Completed",
    renewal_due: "Renewal Due",
    cancelled: "Cancelled",
    sync_error: "Sync Error",
};

export default function AssignmentsView() {
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem("at_classroom_view") || "hub"; } catch { return "hub"; }
    });
    const [bucket, setBucket] = useState("newly_accepted");
    const [search, setSearch] = useState("");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [demoOpen, setDemoOpen] = useState(false);

    const setView = (v) => {
        setViewMode(v);
        try { localStorage.setItem("at_classroom_view", v); } catch { /* ignore */ }
    };

    const openAssignmentById = async (aid) => {
        if (!aid) return;
        try {
            const r = await api.get(`/admin/assignments/${aid}`);
            setDetail(r.data);
        } catch {
            toast.error("Assignment not found");
        }
    };

    const load = () => {
        if (viewMode !== "list") return;
        setLoading(true);
        const p = new URLSearchParams({ bucket });
        if (search.trim()) p.append("search", search.trim());
        api.get(`/admin/assignments?${p.toString()}`)
            .then((r) => {
                setItems(r.data?.items || []);
                setCounts(r.data?.counts || {});
            })
            .catch(() => toast.error("Failed to load assignments"))
            .finally(() => setLoading(false));
    };
    useEffect(load, [bucket, viewMode]);
    useEffect(() => {
        if (viewMode !== "list") return;
        const t = setTimeout(load, search ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const empty = useMemo(() => !loading && items.length === 0, [loading, items]);

    return (
        <div className="space-y-4 classroom-mobile px-3 sm:px-0" data-testid="assignments-view">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-xl sm:text-2xl font-heading font-bold text-[#0F172A]">Classroom & Assignments</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Post-tutor-acceptance workflow. Tutor applications & lead publishing stay in BookMyTutor.</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    {/* View toggle */}
                    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" aria-label="Classroom view">
                        <button
                            type="button"
                            onClick={() => setView("hub")}
                            data-testid="view-toggle-hub"
                            data-active={viewMode === "hub" ? "true" : "false"}
                            aria-pressed={viewMode === "hub"}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${viewMode === "hub" ? "bg-[#0F172A] text-white" : "text-slate-600 hover:text-slate-900"}`}
                        >
                            <LayoutGrid className="w-3.5 h-3.5" /> Hub
                        </button>
                        <button
                            type="button"
                            onClick={() => setView("list")}
                            data-testid="view-toggle-list"
                            data-active={viewMode === "list" ? "true" : "false"}
                            aria-pressed={viewMode === "list"}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${viewMode === "list" ? "bg-[#0F172A] text-white" : "text-slate-600 hover:text-slate-900"}`}
                        >
                            <List className="w-3.5 h-3.5" /> List
                        </button>
                    </div>
                    <Link
                        to="/admin/live-classroom-setup"
                        data-testid="hms-wizard-link"
                        data-cm-primary
                        title="Live Classroom setup"
                        aria-label="Live Classroom setup"
                        className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-2.5 sm:px-3 py-2 rounded-lg text-xs sm:text-xs font-semibold hover:border-cyan-300 hover:text-cyan-800"
                    >
                        <Settings className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Live Classroom</span>
                    </Link>
                    <button
                        type="button"
                        onClick={() => setDemoOpen(true)}
                        data-testid="demo-class-btn"
                        data-cm-primary
                        title="Create demo class"
                        aria-label="Create demo class"
                        className="inline-flex items-center gap-2 bg-fuchsia-600 text-white px-2.5 sm:px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-fuchsia-700"
                    >
                        <Sparkles className="w-4 h-4" /> <span className="hidden sm:inline">Create Demo Class</span><span className="sm:hidden">Demo</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setCreateOpen(true)}
                        data-testid="assignment-create-btn"
                        data-cm-primary
                        title="Log accepted lead"
                        aria-label="Log accepted lead"
                        className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-2.5 sm:px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E293B]"
                    >
                        <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Log Accepted Lead</span><span className="sm:hidden">Lead</span>
                    </button>
                </div>
            </div>

            {viewMode === "hub" ? (
                <ClassroomHub
                    onNavigate={(v) => setView(v)}
                    onOpenAssignment={openAssignmentById}
                />
            ) : (
            <>
            {/* Bucket tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1 px-0.5 -webkit-overflow-scrolling-touch" data-testid="assignment-bucket-tabs">
                {BUCKETS.map((b) => {
                    const active = bucket === b.id;
                    const c = counts[b.id] || 0;
                    const Icon = b.icon;
                    return (
                        <button
                            key={b.id}
                            onClick={() => setBucket(b.id)}
                            data-testid={`bucket-${b.id}`}
                            data-active={active ? "true" : "false"}
                            data-cm-compact
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border whitespace-nowrap transition-colors min-h-[36px] ${
                                active
                                    ? "bg-[#0F172A] text-white border-[#0F172A]"
                                    : `${b.tone} hover:brightness-95`
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {b.label}
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${active ? "bg-white/20" : "bg-white/70"}`}>{c}</span>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute top-2.5 left-3 w-4 h-4 text-slate-400" />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by student, tutor, subject, assignment code, BMT lead ID…"
                    data-testid="assignment-search"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                />
            </div>

            {/* Table / cards */}
            {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : empty ? (
                <div className="text-center py-14 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/40">
                    <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <div className="text-sm font-semibold text-slate-700">No assignments in this bucket yet</div>
                    <div className="text-xs text-slate-500 mt-1">Log an accepted BookMyTutor lead to get started.</div>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto bg-white border border-slate-200 rounded-xl">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-4 py-3">Assignment</th>
                                    <th className="px-4 py-3">Student</th>
                                    <th className="px-4 py-3">Tutor</th>
                                    <th className="px-4 py-3">Subject / Grade</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Accepted</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((a) => (
                                    <tr key={a.id} data-testid={`assignment-row-${a.id}`} className="hover:bg-slate-50">
                                        <td className="px-4 py-3 font-heading font-bold text-[#0F172A]">{a.assignment_code}
                                            <div className="text-[10px] text-slate-400 font-normal font-sans">BMT: {a.bookmytutor_lead_id}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-slate-800">{a.student_name}</div>
                                            <div className="text-xs text-slate-500">{a.city || ""}{a.city && a.state ? ", " : ""}{a.state || ""}</div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-700">{a.tutor_name}</td>
                                        <td className="px-4 py-3 text-xs text-slate-600">
                                            {a.subject || "—"}{a.grade ? ` · ${a.grade}` : ""}{a.board ? ` · ${a.board}` : ""}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">
                                                {STATUS_LABELS[a.status] || a.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formatReceivedAt(a.acceptance_timestamp || a.created_at)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => setDetail(a)}
                                                data-testid={`assignment-open-${a.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#1E3A8A] bg-blue-50 hover:bg-blue-100 rounded-lg"
                                            >
                                                <Eye className="w-3.5 h-3.5" /> Open
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile card list */}
                    <div className="md:hidden space-y-2">
                        {items.map((a) => (
                            <button
                                key={a.id}
                                onClick={() => setDetail(a)}
                                data-testid={`assignment-card-${a.id}`}
                                className="w-full text-left bg-white border border-slate-200 rounded-2xl p-3.5 active:scale-[0.99]"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold text-[#0F172A]">{a.assignment_code}</div>
                                        <div className="text-xs text-slate-500 truncate">BMT: {a.bookmytutor_lead_id}</div>
                                    </div>
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 shrink-0">{STATUS_LABELS[a.status] || a.status}</span>
                                </div>
                                <div className="text-sm text-slate-800 mt-2 font-medium truncate">{a.student_name}</div>
                                <div className="text-xs text-slate-500 truncate">Tutor: {a.tutor_name}</div>
                                <div className="text-xs text-slate-500 truncate mt-0.5">{a.subject || "—"}{a.grade ? ` · ${a.grade}` : ""}</div>
                                <div className="text-[10px] text-slate-400 mt-1">Accepted: {formatReceivedAt(a.acceptance_timestamp || a.created_at)}</div>
                            </button>
                        ))}
                    </div>
                </>
            )}

            </>
            )}

            {detail && (
                <AssignmentDetailModal
                    assignment={detail}
                    onClose={() => setDetail(null)}
                    onSaved={load}
                />
            )}
            {createOpen && (
                <CreateAssignmentDialog
                    onClose={() => setCreateOpen(false)}
                    onCreated={() => { setCreateOpen(false); load(); toast.success("Assignment created"); }}
                />
            )}
            {demoOpen && (
                <CreateDemoClassDialog
                    onClose={() => setDemoOpen(false)}
                    onCreated={() => { load(); }}
                />
            )}
        </div>
    );
}
